// src/contexts/GameContext.jsx
import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { GAME_STATE, TEAMS, BATTLE_LINE, UNIT_COSTS } from '../utils/constants';
import { Unit } from '../models/Unit';
import { createUnit } from '../models/UnitTypes';
import { LevelManager } from '../utils/LevelManager';

export const GameContext = createContext();

export const GameProvider = ({ children }) => {
  // Game state
  const [gameState, setGameState] = useState(GAME_STATE.PLACEMENT);
  const [units, setUnits] = useState({ red: [], blue: [] });
  const [selectedUnitType, setSelectedUnitType] = useState('footman');
  const [currency, setCurrency] = useState(500);
  const [battleLinePosition, setBattleLinePosition] = useState(BATTLE_LINE.START_POSITION);
  const [winner, setWinner] = useState(null);
  const [notification, setNotification] = useState(null);
  const [rewardInfo, setRewardInfo] = useState(null);
  
  // References for game loop
  const canvasRef = useRef({ width: 900, height: 400 });
  const prevTimestampRef = useRef(0);
  const animationRef = useRef(null);
  
  // State managers
  const [levelManager] = useState(() => new LevelManager(Unit));
  const [stageInfo, setStageInfo] = useState(null);
  
  // Deployment grid system
  const [deploymentGrid, setDeploymentGrid] = useState([]);
  const [hoveredCell, setHoveredCell] = useState(null);
  
  // Show notification
  const showNotification = useCallback((message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 5000);
  }, []);
  
  // Initialize game
  const initializeGame = useCallback(() => {
    setBattleLinePosition(BATTLE_LINE.START_POSITION);
    levelManager.reset();
    
    const stageData = levelManager.loadCurrentStage(
      canvasRef.current.width, 
      canvasRef.current.height
    );
    
    if (stageData) {
      setUnits(stageData.units);
      setCurrency(stageData.startingCurrency);
      setStageInfo(levelManager.getCurrentStageInfo());
    }
    
    setDeploymentGrid(prev => prev.map(cell => ({
      ...cell,
      occupied: false,
      unitType: null
    })));
    
    setGameState(GAME_STATE.PLACEMENT);
    setWinner(null);
    setRewardInfo(null);
  }, [levelManager]);
  
  // Create deployment grid
  const createDeploymentGrid = useCallback(() => {
    if (!canvasRef.current || !canvasRef.current.width) return;
    
    const gridCols = 3;
    const gridRows = 5;
    const deploymentZoneWidth = canvasRef.current.width * 0.25;
    
    // Calculate positioning for the grid
    const margin = 0.1; // 10% margin
    const cellWidth = (1 - margin * 2) / gridCols;
    const cellHeight = (1 - margin * 2) / gridRows;
    
    // Create grid cells
    const newGrid = [];
    for (let row = 0; row < gridRows; row++) {
      for (let col = 0; col < gridCols; col++) {
        // Position cells using relative coordinates (0-1 range)
        const xPercent = margin + col * cellWidth + cellWidth / 2;
        const yPercent = margin + row * cellHeight + cellHeight / 2;
        
        // Calculate absolute pixel positions
        const x = deploymentZoneWidth * xPercent;
        const y = canvasRef.current.height * yPercent;
        
        newGrid.push({
          row,
          col,
          x,
          y,
          xPercent,
          yPercent,
          cellWidthPercent: cellWidth,
          cellHeightPercent: cellHeight,
          occupied: false,
          unitType: null
        });
      }
    }
    
    setDeploymentGrid(newGrid);
  }, []);
  
  // Place unit in deployment grid
  const placeUnit = useCallback((cell) => {
    // Validation
    if (gameState !== GAME_STATE.PLACEMENT || !cell || cell.occupied) return;
    
    // Check unit cost
    const unitCost = UNIT_COSTS[selectedUnitType] || 100;
    if (currency < unitCost) {
      showNotification(`Not enough currency! ${selectedUnitType} costs ${unitCost}`);
      return;
    }
    
    // Create new unit at the grid cell position
    const newUnit = createUnit(
      selectedUnitType, 
      TEAMS.RED, 
      cell.x, 
      cell.y, 
      Unit
    );
    
    newUnit.gridPosition = { row: cell.row, col: cell.col };
    
    // Update grid cell status
    setDeploymentGrid(prev => prev.map(c => {
      if (c === cell) {
        return { ...c, occupied: true, unitType: selectedUnitType };
      }
      return c;
    }));
    
    // Update units and currency
    setUnits(prevUnits => ({
      ...prevUnits,
      [TEAMS.RED]: [...prevUnits[TEAMS.RED], newUnit]
    }));
    
    setCurrency(prevCurrency => prevCurrency - unitCost);
  }, [gameState, selectedUnitType, currency, showNotification]);
  
  // Delete unit
  const deleteUnit = useCallback((x, y) => {
    if (gameState !== GAME_STATE.PLACEMENT) return;
    
    setUnits(prevUnits => {
      const newUnits = { ...prevUnits };
      
      // Find unit to delete
      const unitToDelete = newUnits[TEAMS.RED].find(unit => unit.containsPoint(x, y));
      
      if (unitToDelete) {
        // Refund the unit cost
        const unitCost = UNIT_COSTS[unitToDelete.unitKey] || 0;
        setCurrency(prev => prev + unitCost);
        
        // Free up the grid cell
        if (unitToDelete.gridPosition) {
          setDeploymentGrid(prev => prev.map(cell => {
            if (cell.row === unitToDelete.gridPosition.row && cell.col === unitToDelete.gridPosition.col) {
              return { ...cell, occupied: false, unitType: null };
            }
            return cell;
          }));
        }
        
        // Remove the unit
        newUnits[TEAMS.RED] = prevUnits[TEAMS.RED].filter(unit => unit !== unitToDelete);
      }
      
      return newUnits;
    });
  }, [gameState]);
  
  // Start battle
  const startBattle = useCallback(() => {
    if (gameState === GAME_STATE.PLACEMENT) {
      // Ensure player has deployed at least one unit
      if (units.red.length === 0) {
        showNotification("Deploy at least one unit before starting the battle!");
        return;
      }
      
      // If no blue units, reload from stage data
      if (units.blue.length === 0) {
        const stageData = levelManager.loadCurrentStage(
          canvasRef.current.width, 
          canvasRef.current.height
        );
        
        if (stageData && stageData.units.blue.length > 0) {
          setUnits(prevUnits => ({
            red: prevUnits.red,
            blue: stageData.units.blue
          }));
        }
      }
      
      setGameState(GAME_STATE.BATTLE);
    }
  }, [gameState, units, levelManager, showNotification]);
  
  // Award victory currency
  const awardVictoryCurrency = useCallback(() => {
    // Base reward
    const baseReward = 200;
    
    // Performance bonus based on battle line position
    const performanceBonus = Math.floor((battleLinePosition - 50) * 5);
    
    // Stage bonus
    const stageBonus = stageInfo ? (stageInfo.levelNumber * 50 + stageInfo.stageNumber * 25) : 0;
    
    // Total reward
    const totalReward = baseReward + Math.max(0, performanceBonus) + stageBonus;
    
    setCurrency(prevCurrency => prevCurrency + totalReward);
    
    // Set reward info
    setRewardInfo({
      baseReward,
      performanceBonus: Math.max(0, performanceBonus),
      stageBonus,
      totalReward
    });
    
    showNotification(`Victory! Earned ${totalReward} currency`);
  }, [battleLinePosition, stageInfo, showNotification]);
  
  // Load next stage
  const loadNextStage = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    const result = levelManager.advanceStage();
    const stageData = levelManager.loadCurrentStage(
      canvasRef.current.width, 
      canvasRef.current.height
    );
    
    if (stageData) {
      // Filter surviving units (health > 0)
      const survivingRedUnits = units.red.filter(unit => unit.health > 0);
      
      // Reset grid occupation
      setDeploymentGrid(prev => prev.map(cell => ({
        ...cell,
        occupied: false,
        unitType: null
      })));
      
      // Reposition surviving units to their original grid positions
      const repositionedRedUnits = survivingRedUnits.map(unit => {
        if (unit.gridPosition) {
          // Find the corresponding grid cell
          const gridCell = deploymentGrid.find(cell => 
            cell.row === unit.gridPosition.row && cell.col === unit.gridPosition.col
          );
          
          if (gridCell) {
            // Recalculate unit position based on current canvas size
            const deploymentZoneWidth = canvasRef.current.width * 0.25;
            unit.x = deploymentZoneWidth * gridCell.xPercent;
            unit.y = canvasRef.current.height * gridCell.yPercent;
            
            // Mark grid cell as occupied
            setDeploymentGrid(prev => prev.map(cell => {
              if (cell.row === unit.gridPosition.row && cell.col === unit.gridPosition.col) {
                return { ...cell, occupied: true, unitType: unit.unitKey };
              }
              return cell;
            }));
          }
        }
        return unit;
      });
      
      // Load new stage data with repositioned surviving units
      setUnits({
        red: repositionedRedUnits,
        blue: stageData.units.blue
      });
      
      const bonusCurrency = Math.floor(currency * 0.5);
      
      setCurrency(stageData.startingCurrency + bonusCurrency);
      setStageInfo(levelManager.getCurrentStageInfo());
      
      setGameState(GAME_STATE.PLACEMENT);
      setWinner(null);
      setBattleLinePosition(BATTLE_LINE.START_POSITION);
      setRewardInfo(null);
      
      if (result === "LEVEL_COMPLETE") {
        showNotification(`Level ${stageInfo.levelNumber} complete! Moving to level ${stageInfo.levelNumber + 1}`);
      } else if (result === "GAME_COMPLETE") {
        showNotification("Congratulations! You've completed the game!");
      }
    }
  }, [levelManager, currency, stageInfo, units.red, deploymentGrid, showNotification]);
  
  // Update battle simulation
  const updateSimulation = useCallback((timestamp, prevTimestamp) => {
    if (gameState !== GAME_STATE.BATTLE) return units;
    
    const newUnits = {
      red: [...units.red],
      blue: [...units.blue]
    };
    
    // Update each unit
    for (const team of [TEAMS.RED, TEAMS.BLUE]) {
      const enemyTeam = team === TEAMS.RED ? TEAMS.BLUE : TEAMS.RED;
      
      for (const unit of newUnits[team]) {
        // Find target if needed
        if (!unit.target || unit.target.health <= 0) {
          unit.target = unit.findNearestEnemy(newUnits[enemyTeam]);
        }
        
        // Move toward target
        unit.moveTowardTarget();
        
        // Attack target
        unit.attack(timestamp);
        
        // Check if target is dead
        if (unit.target && unit.target.health <= 0) {
          // Remove dead unit
          newUnits[enemyTeam] = newUnits[enemyTeam].filter(u => u !== unit.target);
          unit.target = null;
        }
      }
    }
    
    return newUnits;
  }, [units, gameState]);
  
  // Update battle line
  const updateBattleLine = useCallback(() => {
    if (gameState !== GAME_STATE.BATTLE) return;
    
    // Find the furthest red unit and furthest blue unit
    let furthestRedX = 0;
    let furthestBlueX = canvasRef.current.width;
    
    units.red.forEach(unit => {
      if (unit.x > furthestRedX) furthestRedX = unit.x;
    });
    
    units.blue.forEach(unit => {
      if (unit.x < furthestBlueX) furthestBlueX = unit.x;
    });
    
    // Calculate battle line position
    let newPosition;
    if (units.red.length === 0) {
      // If no red units, line moves toward spawn
      newPosition = Math.max(battleLinePosition - 0.5, BATTLE_LINE.LOSE_THRESHOLD);
    } else if (units.blue.length === 0) {
      // If no blue units, line moves toward enemy base
      newPosition = Math.min(battleLinePosition + 0.5, BATTLE_LINE.WIN_THRESHOLD);
    } else {
      // Normal calculation
      const redWeight = 0.6;
      const blueWeight = 0.4;
      
      const redPosition = (furthestRedX / canvasRef.current.width) * 100;
      const bluePosition = (furthestBlueX / canvasRef.current.width) * 100;
      
      newPosition = (redPosition * redWeight + bluePosition * blueWeight);
    }
    
    // Update with smoothing
    setBattleLinePosition(prevPos => {
      return Math.max(BATTLE_LINE.LOSE_THRESHOLD, 
                Math.min(BATTLE_LINE.WIN_THRESHOLD, 
                        prevPos * 0.8 + newPosition * 0.2));
    });
  }, [units, battleLinePosition, gameState]);
  
  // Check win/lose conditions
  const checkWinLoseConditions = useCallback(() => {
    if (gameState !== GAME_STATE.BATTLE) return;
    
    // Win condition: Battle line pushed to enemy base
    if (battleLinePosition >= BATTLE_LINE.WIN_THRESHOLD) {
      setGameState(GAME_STATE.GAME_OVER);
      setWinner(TEAMS.RED);
      awardVictoryCurrency();
      return;
    }
    
    // Win condition: All enemies destroyed
    if (units.blue.length === 0) {
      setGameState(GAME_STATE.GAME_OVER);
      setWinner(TEAMS.RED);
      awardVictoryCurrency();
      return;
    }
    
    // Lose condition: Battle line pushed back to spawn
    if (battleLinePosition <= BATTLE_LINE.LOSE_THRESHOLD) {
      setGameState(GAME_STATE.GAME_OVER);
      setWinner(TEAMS.BLUE);
      showNotification("Defeat! Your forces were pushed back.");
      return;
    }
    
    // Lose condition: All player units destroyed
    if (units.red.length === 0) {
      setGameState(GAME_STATE.GAME_OVER);
      setWinner(TEAMS.BLUE);
      showNotification("Defeat! All your units were destroyed.");
      return;
    }
  }, [gameState, battleLinePosition, units, awardVictoryCurrency, showNotification]);
  
  // Game loop
  const gameLoop = useCallback((timestamp) => {
    if (gameState === GAME_STATE.BATTLE) {
      // Update unit behavior
      const newUnits = updateSimulation(timestamp, prevTimestampRef.current);
      setUnits(newUnits);
      prevTimestampRef.current = timestamp;
      
      // Update battle line
      updateBattleLine();
      
      // Check win/lose conditions
      checkWinLoseConditions();
    }
    
    // Continue the animation loop
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [gameState, updateSimulation, updateBattleLine, checkWinLoseConditions]);
  
  // Initialize game
  useEffect(() => {
    initializeGame();
    createDeploymentGrid();
  }, [initializeGame, createDeploymentGrid]);
  
  // Start game loop
  useEffect(() => {
    animationRef.current = requestAnimationFrame(gameLoop);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameLoop]);
  
  // Set canvas reference
  const setCanvasRef = useCallback((width, height) => {
    canvasRef.current = { width, height };
    createDeploymentGrid();
  }, [createDeploymentGrid]);
  
  // Context value
  const contextValue = {
    // Game state
    gameState,
    units,
    selectedUnitType,
    currency,
    battleLinePosition,
    winner,
    notification,
    rewardInfo,
    stageInfo,
    deploymentGrid,
    hoveredCell,
    
    // Game actions
    setSelectedUnitType,
    setHoveredCell,
    placeUnit,
    deleteUnit,
    startBattle,
    loadNextStage,
    initializeGame,
    setCanvasRef
  };
  
  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

export default GameProvider;